import { AddUserClientRequest } from './../../_models/admin/add-user-client-request.model';
import { SelectedUserClientToAdd } from './../../_models/admin/selected-user-client-to-add.model';
import { ClientInfoForUserAdd } from './../../_models/user';
import { RoleService } from './../../_services/role.service';
import { UserClient } from './../../_models/admin/user-client.model';
import { ClientUser } from './../../_models/admin/client-user.model';
import { Component, OnInit, Optional, ViewChild} from '@angular/core';
import { BaseComponent } from '../../base.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { ClientService } from '../../_services/client.service';
import { UpdateClientUserRoleRequest } from './../../_models/admin/update-client-user-role-request.model';
import { UpdateUserMfaRequiredRequest } from './../../_models/admin/update-user-mfa-required-request.model';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { MatSort } from '@angular/material/sort';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { ClientUserSearchFilters } from '../../_models/admin/client-user-search-filters.model';
import { AuthMethod } from '../../_models/admin/user-maintenance.models';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { BookingAccessFeature } from '../../_constants/booking-access';

@Component({
    selector: 'client-admin',
    templateUrl: 'client-admin.component.html',
    styleUrls: ['client-admin.component.css'],
    standalone: false
})

export class ClientAdminComponent extends BaseComponent implements OnInit {
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  
  public clientForm: UntypedFormGroup;

  public clientUsers: Array<ClientUser> = new Array<ClientUser>();
  public showUserPanel: boolean = false;
  public errorMsg: string = '';
  public addClientRows: Array<boolean> = new Array<boolean>();
  public clientList: ClientInfoForUserAdd[];

  public paginatorPage: number = 1;

  /** Whether each user's client table shows the Can see column and the bookings they are named on:
   * only while clientusers.booking-grants is on and the signed-in user holds its role feature. The API checks both again. */
  public bookingAccessVisible: boolean = false;

  /**
   * The filters.client-users switch. Off, the screen is exactly as it was before the filter work: no
   * brand, role, status, sign-in or never-signed-in filters, and none of their request parameters.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersClientUsers;
  public filtersEnabled: boolean = false;

  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** Client role code (CLIENT, CUSTODIAN, CLREPORTS); empty string is any. */
  public roleCode: string = '';
  /** 'true' (active) / 'false' (deactivated); empty string is either. */
  public activeFilter: string = '';
  /** Sign-in method bucket; empty string is any. */
  public authMethod: AuthMethod | '' = '';
  public neverSignedIn: boolean = false;
  /** search.autocomplete.client-users: the search box suggests the names in clientList. Off, it is a plain box. */
  public autocompleteEnabled: boolean = false;
  /** The distinct client names in clientList, for the search box's suggestions. */
  public clientNames: string[] = [];

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    brand: 'number[]', role: 'string', status: 'string', signIn: 'string', neverSignedIn: 'boolean'
  };
  /** Remembered filters and filter state in the URL; used only while filters.client-users is on. */
  private filterUx: ScreenFilterUx;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private clientService: ClientService,
    private roleService: RoleService,
    private successModalHelper: SuccessModalHelperService,
    public router: Router,
    private featureHelperService: FeatureHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null
  ) {
    super();
    this.clientForm = this.formBuilder.group({
      searchPhrase : []
    });
  }

  ngOnInit() {
 
    let featureList = new Array<string>();
    featureList.push("ClientAdminEdit");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "ClientAdminEdit")[0].enabled) {
      console.error("User does not have the required permissions to view the client admin screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
      this.authenticationStateHelper.unauthorizedToken = true;
    }

    this.clientService.GetClients(this.authService.userSessionData.currentUser.id).subscribe((data: ClientInfoForUserAdd[]) => {

      data.forEach(item => {
        item.disableReferral = true;
      });

      this.clientList = data;
      this.clientList.forEach(c => c.dropdownDisplay = c.clientName + " ID: " + c.clientId)
      this.clientNames = Array.from(new Set(this.clientList.map(c => (c.clientName ?? '').trim()).filter(name => !!name)));
    });

    this.filterUx = new ScreenFilterUx(this.filtersKey, ClientAdminComponent.filterSpec, this.router, this.route);

    if (this.featureHelperService.hasFeature(BookingAccessFeature)) {
      this.featureSwitches.isEnabled(FeatureSwitchKeys.clientUsersBookingGrants)
        .subscribe(enabled => this.bookingAccessVisible = enabled);
    }
    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteClientUsers)
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(this.filtersKey).subscribe(enabled => {
      this.filtersEnabled = enabled;
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      const initial = enabled ? this.filterUx.initialState() : null;
      if (initial) {
        this.applyFilterState(initial);
        this.search();
      }
    });

    this.search();
  }

  public submit(): void {
    let clientsData = {
      clientUserClients: this.clientUsers,
      createdBy: this.authService.userSessionData.currentUser.id
    };
    this.clientService.UpdateClientUsers(clientsData).subscribe(result => {
      this.successModalHelper.show("Client access details updated");
      this.clientService.SendEmailsToClientUsers(clientsData);
      this.search();
      this.clientList.forEach((item: ClientInfoForUserAdd) => {
        item.disableReferral = true;
        item.viewReferrals = false;
      });
    },
      error => {
            this.errorModalHelper.show(error.message, error);
      });
  }

  public addUser(): void {
    this.showUserPanel = true;
  }

  public onClosed(event): void {
    this.showUserPanel = false;
    this.clientList.forEach((item: ClientInfoForUserAdd) => {
      item.disableReferral = true;
      item.viewReferrals = false;
    });
  }

  public search(): void {
    let searchPhrase = this.clientForm.value.searchPhrase;
    if(searchPhrase == null) {
     searchPhrase = "";
    }
    let userId = this.authService.userSessionData.currentUser.id;
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
    this.clientService.clientUserSearch(searchPhrase, userId, this.buildFilters()).subscribe((data: ClientUser[]) => {
      this.errorMsg = '';

      this.addClientRows = new Array<boolean>();
      data.forEach(() => {
        this.addClientRows.push(true);
      });

      this.clientUsers = data;
      
      if (data == null || data.length === 0) {
        this.errorMsg = 'No data found';
      }
    },
      error => {
        this.errorModalHelper.show(error.message, error);
      })
  }

  /** Clear all: back to the screen's defaults, forgotten, and out of the URL. */
  public clearAll(): void {
    this.clientForm.get('searchPhrase').setValue(null);
    this.applyFilterState({});
    this.filterUx.clear();
    this.search();
  }

  private filterState(): FilterState {
    return {
      brand: this.companyIds,
      role: this.roleCode,
      status: this.activeFilter,
      signIn: this.authMethod,
      neverSignedIn: this.neverSignedIn || null
    };
  }

  private applyFilterState(state: FilterState): void {
    this.companyIds = (state.brand as number[]) ?? [];
    this.roleCode = (state.role as string) ?? '';
    this.activeFilter = (state.status as string) ?? '';
    this.authMethod = ((state.signIn as string) ?? '') as AuthMethod | '';
    this.neverSignedIn = state.neverSignedIn === true;
  }

  /** The switched filters, or undefined when filters.client-users is off so none are sent. */
  private buildFilters(): ClientUserSearchFilters | undefined {
    if (!this.filtersEnabled) {
      return undefined;
    }
    return {
      companyIds: this.companyIds,
      roleCode: this.roleCode || null,
      active: this.activeFilter === '' ? null : this.activeFilter === 'true',
      authMethod: this.authMethod || null,
      neverSignedIn: this.neverSignedIn ? true : null
    };
  }

  /** Emails a client user who has not set a password a new set-password link. Shown only while hasPassword is false. */
  public onResendCode(element: ClientUser) {
    this.userService.resendActivationCodeByUserId(element.userId).subscribe(data => {
      this.successModalHelper.show('Activation email sent to ' + element.emailAddress);
      element.disableResendCode = true;
    },
      error => {
            this.errorModalHelper.show(error.message, error);
      }
    )
  }
  
  public assignMfaRequired(user: ClientUser, event: any): void {
    let updateUserMfaRequiredRequest = new UpdateUserMfaRequiredRequest();
    updateUserMfaRequiredRequest.id = user.userId;
    updateUserMfaRequiredRequest.newStatus = event.checked;

    this.userService.updateUserMfaRequired(updateUserMfaRequiredRequest).subscribe(success => {},
      error => {
        this.errorModalHelper.show("Could not update mfa. Please try again.", error);
        console.error(error);
      });
  }

  public assignClientRole(client: UserClient, event: any, roleType: string): void {
    let updateClientUserRoleRequest = new UpdateClientUserRoleRequest();
    updateClientUserRoleRequest.id = client.clientUserClientId;
    updateClientUserRoleRequest.roleName = roleType;
    updateClientUserRoleRequest.newStatus = event.checked;

    switch (roleType) {
      case "REPORT":
        client.isReport = event.checked;
        break;
      case "ADMIN":
        client.isAdmin = event.checked;
      break;
      case "CUSTODIAN":
        client.isCustodian = event.checked;
        break;
      case "REFERRAL":
        client.viewReferralsOnly = event.checked;
        break;
    
      default:
        break;
    }

    this.roleService.updateUserClientRole(updateClientUserRoleRequest).subscribe(success => {},
      error => {
        this.errorModalHelper.show("Could not update role. Please try again.", error);
        console.error(error);
      });
  }

  public showAddClientRow(index: number): void {
    this.addClientRows[index] = false;
  }

  public clientAddCancelled(addCancelled: boolean, index: number): void {
    if (addCancelled) {
      this.addClientRows[index] = true;
    }
  } 
  
  public clientAdded(selectedClient: SelectedUserClientToAdd, user: ClientUser, index: number): void {
    let request: AddUserClientRequest = new AddUserClientRequest();
    request.userId = user.userId;
    request.clientId = selectedClient.clientId;
    request.adminRole = selectedClient.isAdmin;
    request.reportingRole = selectedClient.isReport;
    request.viewReferrals = selectedClient.viewReferralsOnly;
    request.custodianRole = selectedClient.isCustodian;

    this.userService.addNewUserClient(request).subscribe(success => {
      this.search();
    }, error => {
      this.errorModalHelper.show("An error has occurred. Please contact your administrator.", error);
      console.error(error);
    });
  }

}


